import React from "react";
import { graphql } from "gatsby";
import { Box, styled, Typography, useTheme } from "@material-ui/core";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ArrowDown from "../../components/icons/arrow-down";
import { Variant } from "@material-ui/core/styles/createTypography";

const CustomAccordion = styled(Accordion)(({ theme }) => ({
  background: "transparent",
  boxShadow: "none",
  borderTop: `1px solid ${theme.palette.grey[500]}`,
  "&:before": {
    background: "none"
  },
  "&:first-child": {
    borderTop: `0px`
  },
  "&:last-child": {
    borderBottom: `1px solid ${theme.palette.grey[500]}`
  },
  "&.Mui-expanded": {
    margin: 0
  },
  "& .MuiAccordionSummary-root": {
    padding: 0,
    alignItems: "center",
    "& .MuiAccordionSummary-expandIcon": {
      marginTop: theme.spacing(0.5)
    }
  },
  "& .MuiAccordionSummary-content": {
    margin: "24px 0"
  },
  "& .MuiAccordionSummary-expandIcon": {
    marginTop: "16px !important",
    color: "currentColor"
  },
  "& .MuiAccordionDetails-root": {
    padding: "8px 0 36px 0",
    "& p:not(:last-child)": {
      marginBottom: theme.spacing(2)
    }
  },
  "& img": {
    maxWidth: "100%",
    "&:not(:last-child)": {
      marginBottom: theme.spacing(2)
    }
  }
}));

function FaqEmbeddableSection({
  title,
  questionsAnswers,
  maxWidthPx,
  defaultCollapsed,
  color,
  titleTextAlign,
  titleFontSize
}: Partial<Queries.FaqEmbeddableSectionFragmentFragment>) {
  const theme = useTheme();
  const typoClassName = color === "white" ? "white" : undefined;
  const accordionColor = color === "white" ? "#585858" : undefined;
  const iconColor = color === "white" ? theme.palette.grey[500] : undefined;

  return (
    <Box
      display="flex"
      flexDirection="column"
      maxWidth={maxWidthPx || "none"}
      marginX="auto"
      marginTop={0}
      marginBottom={0}
    >
      {title && (
        <Box
          marginBottom={{ xs: 2, md: 2 }}
          textAlign={titleTextAlign === "center" ? "center" : "left"}
        >
          <Typography
            variant={(titleFontSize as Variant) || "h5"}
            className={typoClassName}
          >
            {title}
          </Typography>
        </Box>
      )}
      <Box>
        {questionsAnswers.map((questionAnswer, index) => (
          <CustomAccordion
            key={questionAnswer.question}
            defaultExpanded={!defaultCollapsed}
            square
            style={{ color: iconColor, borderColor: accordionColor }}
            data-faq-emeddable-section-index={index}
          >
            <AccordionSummary expandIcon={<ArrowDown />}>
              <Typography variant="h6" className={typoClassName}>
                {questionAnswer.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                component="div"
                dangerouslySetInnerHTML={{
                  __html: questionAnswer.answerNode.childMarkdownRemark.html
                }}
                className={typoClassName}
              />
            </AccordionDetails>
          </CustomAccordion>
        ))}
      </Box>
    </Box>
  );
}

export const FaqEmbeddableSectionFragment = graphql`
  fragment FaqEmbeddableSectionFragment on DatoCmsCModuleFaqSection {
    title
    titleTextAlign
    titleFontSize
    questionsAnswers {
      question
      answerNode {
        childMarkdownRemark {
          html
        }
      }
    }
    maxWidthPx
    defaultCollapsed
    color
    container
    containerDisableGutters
    containerBackgroundColor
    innerContainer
    visibilitySettings {
      ...ContentModuleVisibilitySettingFragment
    }
    model {
      apiKey
    }
  }
`;

export default React.memo(FaqEmbeddableSection);
